import { useMemo } from 'react';

import type { JobFamily } from '~/shared/data-contract/job-family';

import { useChangesetState } from '../onboarding/OnboardingChangesetProvider';
import { useFamiliesWithClusters } from './use-families-with-clusters';

export type UseJobFamilyDetailsReturn = {
    readonly data: JobFamily | undefined;
    readonly error: Error | null;
    readonly isError: boolean;
    readonly isLoading: boolean;
};

/** Resolves family by ID from changeset first, then backend. */
export const useJobFamilyDetails = (architectureId: string, familyId: string): UseJobFamilyDetailsReturn => {
    const { addedRows } = useChangesetState();
    const fromChangeset = useMemo(() => addedRows.find((f) => f.id === familyId)?.data, [addedRows, familyId]);
    const {
        data: familiesWithClusters,
        error,
        isError,
        isLoading,
    } = useFamiliesWithClusters(architectureId, {
        enabled: fromChangeset === undefined,
    });

    const fromServer = useMemo(() => {
        const family = familiesWithClusters?.find((f) => f.id === familyId);
        if (family === undefined) {
            return undefined;
        }
        const { clusters: _clusters, ...rest } = family;
        return rest;
    }, [familiesWithClusters, familyId]);

    const data = fromChangeset ?? fromServer;

    if (fromChangeset !== undefined) {
        return { data: fromChangeset, error: null, isError: false, isLoading: false };
    }

    return {
        data,
        error: error ?? null,
        isError,
        isLoading,
    };
};
